import { createFileRoute } from '@tanstack/react-router';
import { Lock, LockOpen } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/danger/ConfirmDialog';
import { ActionGroup, DangerButton } from '@/components/danger/DangerButton';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { SkeletonTable } from '@/components/states/Skeleton';
import {
  playerLabel,
  useAssessRosterChange,
  useRosters,
  useSwapPlayers,
  type AssessmentPayload,
  type LineupSlot,
  type WeekContextPayload,
} from '@/features/leagues/rosters';
import { ApiError } from '@/lib/api/client';

export const Route = createFileRoute('/leagues/$leagueId/rosters')({
  component: RostersScreen,
});

/**
 * The screen the on-call engineer opens most.
 *
 * Two things are structural rather than decorative:
 *
 *  - **The lock banner is the first thing on the page.** Every action below it is
 *    read in that context, and the brief's whole premise is that an operator must
 *    know the week state at the moment of acting.
 *  - **The weekly lineup sits beside the roster.** The roster is who the squad owns;
 *    the lineup is what is actually scoring. Showing only the roster is how someone
 *    "fixes" a player who was never in the lineup to begin with.
 */
function RostersScreen() {
  const { leagueId } = Route.useParams();
  const [squadId, setSquadId] = useState<string | undefined>();
  const { data, isPending, isError, error, refetch } = useRosters(leagueId, squadId);

  const [pendingSwap, setPendingSwap] = useState<{
    out: LineupSlot;
    in: LineupSlot;
    assessment: AssessmentPayload;
  } | null>(null);

  const assess = useAssessRosterChange(leagueId);
  const swap = useSwapPlayers(leagueId);

  async function beginSwap(starter: LineupSlot, benchPlayer: LineupSlot) {
    if (!data?.squad || !data.weekContext?.week) return;

    try {
      // Server decides the tier and the consequences. The dialog only renders them.
      const assessment = await assess.mutateAsync({
        squadId: data.squad.id,
        week: data.weekContext.week,
        changeKind: 'swap',
      });
      setPendingSwap({ out: starter, in: benchPlayer, assessment });
    } catch {
      toast.error('Could not assess this change', {
        description: 'The confirmation was not shown, so nothing was altered.',
      });
    }
  }

  function confirmSwap(reason: string) {
    if (!pendingSwap || !data?.squad || !data.weekContext?.week) return;

    swap.mutate(
      {
        squadId: data.squad.id,
        week: data.weekContext.week,
        slot: pendingSwap.out.slot,
        outPlayerId: pendingSwap.out.playerId,
        inPlayerId: pendingSwap.in.playerId,
        reason,
      },
      {
        onSuccess: (result) => {
          setPendingSwap(null);
          void refetch();
          toast.success('Roster swap applied', {
            description:
              result.enqueuedJobs.length > 0
                ? `Re-enqueued: ${result.enqueuedJobs.join(', ')}`
                : undefined,
          });
        },
        onError: (mutationError) => {
          // Keep the dialog open on failure. Closing it would leave the operator
          // unsure whether the action landed, which is the exact anxiety the brief
          // says destructive actions must never create.
          if (mutationError instanceof ApiError && mutationError.status === 501) {
            toast.error('Not available yet', { description: mutationError.message });
          }
        },
      },
    );
  }

  if (isError) {
    return <ErrorState error={error} subject="rosters" onRetry={() => void refetch()} />;
  }

  if (isPending) {
    return <SkeletonTable rows={10} columns={5} />;
  }

  if (!data.squad || data.squads.length === 0) {
    return (
      <EmptyState
        title="No squads in this league"
        description="Rosters appear once squads have joined and the season has a roster to show."
      />
    );
  }

  const week = data.weekContext;
  const swapError = swap.error;

  return (
    <div className="flex flex-col gap-3.5">
      {week && <LockBanner week={week} />}

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2">
          <span
            className="font-head text-tx3 text-[11px] font-semibold uppercase"
            style={{ letterSpacing: '0.16em' }}
          >
            Squad
          </span>
          <select
            value={data.squad.id}
            onChange={(event) => setSquadId(event.target.value)}
            className="border-bd bg-bg-alt text-tx h-11 rounded-lg border px-2.5 text-[13px] outline-none"
          >
            {data.squads.map((squad) => (
              <option key={squad.id} value={squad.id}>
                {squad.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid gap-3.5 xl:grid-cols-2">
        <LineupPanel
          title={`Week ${String(week?.week ?? '?')} lineup`}
          subtitle="what is actually scoring"
          slots={data.lineup?.starters ?? []}
          emptyLabel="No lineup submitted for this week."
        />
        <LineupPanel
          title="Bench & IR"
          subtitle="on the roster, not scoring"
          slots={data.lineup?.bench ?? []}
          emptyLabel="Nothing on the bench."
        />
      </div>

      <ActionGroup tier={1}>
        <DangerButton tier={1} onClick={() => void refetch()}>
          Refresh
        </DangerButton>
        <DangerButton tier={1}>Export lineups</DangerButton>
      </ActionGroup>

      <ActionGroup tier={3} hint={week?.isLocked ? 'bypasses the lock' : 'roster overrides'}>
        <DangerButton
          tier={3}
          loading={assess.isPending}
          disabled={
            !data.lineup || data.lineup.starters.length === 0 || data.lineup.bench.length === 0
          }
          onClick={() => {
            const starter = data.lineup?.starters[0];
            const benchPlayer = data.lineup?.bench[0];
            if (starter && benchPlayer) void beginSwap(starter, benchPlayer);
          }}
        >
          Swap two players
        </DangerButton>
        <DangerButton tier={3} disabled>
          Drop a player
        </DangerButton>
      </ActionGroup>

      {pendingSwap && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !swap.isPending) {
              setPendingSwap(null);
              swap.reset();
            }
          }}
          tier={pendingSwap.assessment.tier}
          title={
            pendingSwap.assessment.isLocked
              ? 'Swap two players during a locked week'
              : 'Swap two players'
          }
          summary={
            <>
              Moving <strong className="text-tx">{playerLabel(pendingSwap.out)}</strong> out of{' '}
              <strong className="text-tx">{pendingSwap.out.slot}</strong> and{' '}
              <strong className="text-tx">{playerLabel(pendingSwap.in)}</strong> in, for{' '}
              <strong className="text-tx">{data.squad.name}</strong> in week{' '}
              {String(week?.week ?? '?')}.
            </>
          }
          consequences={pendingSwap.assessment.consequences}
          typeToConfirmValue={pendingSwap.assessment.tier === 3 ? data.squad.name : undefined}
          typeToConfirmLabel="squad name"
          confirmLabel={
            pendingSwap.assessment.isScored ? 'Apply swap & re-score' : 'Apply swap'
          }
          isPending={swap.isPending}
          isIndeterminate={swapError instanceof ApiError && swapError.isIndeterminate}
          errorMessage={swapError instanceof Error ? swapError.message : null}
          onConfirm={confirmSwap}
        >
          <RosterDiff
            slot={pendingSwap.out.slot}
            before={playerLabel(pendingSwap.out)}
            after={playerLabel(pendingSwap.in)}
          />
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Lock state, stated first and in plain language.
 *
 * Deliberately not a small chip: this is the single fact that determines whether the
 * actions below are ordinary or season-corrupting.
 */
function LockBanner({ week }: { week: WeekContextPayload }) {
  const locked = week.isLocked;
  const scored = week.scoredMatchups > 0 || week.status === 'complete';

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border px-3.5 py-2.5"
      style={{
        borderColor: locked ? 'rgba(var(--err-rgb), 0.4)' : 'var(--bd)',
        background: locked ? 'rgba(var(--err-rgb), 0.07)' : 'var(--card)',
      }}
    >
      {locked ? (
        <Lock size={15} className="text-err flex-none" aria-hidden />
      ) : (
        <LockOpen size={15} className="text-tx3 flex-none" aria-hidden />
      )}

      <span
        className="text-[13.5px] font-medium"
        style={{ color: locked ? 'var(--err-soft)' : 'var(--tx)' }}
      >
        {scored
          ? `Week ${String(week.week ?? '?')} is locked and already scored`
          : locked
            ? `Week ${String(week.week ?? '?')} is locked`
            : `Week ${String(week.week ?? '?')} is open`}
      </span>

      {week.status && <StateChip state={week.status} size="sm" />}

      <span className="mesh-mono text-tx3 text-[11px]">
        {week.affectedMatchups} matchup{week.affectedMatchups === 1 ? '' : 's'}
        {week.scoredMatchups > 0 ? ` · ${String(week.scoredMatchups)} scored` : ''}
      </span>

      {week.lockAt && (
        <span className="mesh-mono text-tx3 text-[11px]">
          locks {week.lockAt.replace('T', ' ').slice(0, 16)}Z
        </span>
      )}
    </div>
  );
}

function LineupPanel({
  title,
  subtitle,
  slots,
  emptyLabel,
}: {
  title: string;
  subtitle: string;
  slots: LineupSlot[];
  emptyLabel: string;
}) {
  return (
    <section className="border-bd bg-card overflow-hidden rounded-xl border">
      <div className="border-bd flex items-baseline gap-2 border-b px-3.5 py-2.5">
        <h2
          className="font-head text-tx text-[13px] font-semibold uppercase"
          style={{ letterSpacing: '0.14em' }}
        >
          {title}
        </h2>
        <span className="text-tx3 text-[11.5px]">{subtitle}</span>
      </div>

      {slots.length === 0 ? (
        <p className="text-tx3 px-3.5 py-6 text-center text-[12.5px]">{emptyLabel}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-max border-collapse text-left">
            <thead>
              <tr className="border-bd bg-elev border-b">
                {['Slot', 'Player', 'Pos', 'Team', 'Inj'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="font-head text-tx3 px-3 py-1.5 text-[10.5px] font-semibold uppercase"
                    style={{ letterSpacing: '0.14em' }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((slot) => (
                <tr
                  key={`${slot.slot}-${String(slot.playerId)}`}
                  className="border-bd/60 border-b last:border-b-0"
                >
                  <td className="mesh-mono text-tx3 px-3 py-2 text-[11.5px]">{slot.slot}</td>
                  <td className="text-tx px-3 py-2 text-[12.5px]">{playerLabel(slot)}</td>
                  <td className="text-tx2 px-3 py-2 text-[12px]">{slot.position ?? '—'}</td>
                  <td className="text-tx2 px-3 py-2 text-[12px]">{slot.team ?? '—'}</td>
                  <td className="px-3 py-2 text-[12px]">
                    {slot.injuryStatus ? (
                      <span className="text-warn">{slot.injuryStatus}</span>
                    ) : (
                      <span className="text-tx3">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RosterDiff({ slot, before, after }: { slot: string; before: string; after: string }) {
  return (
    <div className="border-bd overflow-hidden rounded-lg border">
      <div
        className="font-head border-bd bg-elev text-tx3 grid grid-cols-[56px_1fr_1fr] gap-2 border-b px-2.5 py-1.5 text-[10.5px] font-semibold uppercase"
        style={{ letterSpacing: '0.14em' }}
      >
        <span>Slot</span>
        <span>Before</span>
        <span>After</span>
      </div>
      <div
        className="grid grid-cols-[56px_1fr_1fr] gap-2 px-2.5 py-2 text-[12.5px]"
        style={{ background: 'rgba(var(--err-rgb), 0.06)' }}
      >
        <span className="mesh-mono text-tx3">{slot}</span>
        <span className="text-tx3 line-through">{before}</span>
        <span className="text-tx font-medium">{after}</span>
      </div>
    </div>
  );
}
